import React, { FC, useState } from 'react';
import Files from 'react-files';
import { useResizeDetector } from 'react-resize-detector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBroom,
  faCheck,
  faEye,
  faEyeSlash,
  faGear,
  faUpload,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import styles from './Tests.module.scss';
import { runTest, TestResult } from '../../tests/tests';
import aggregateResults from './aggregrateResults';
import { drawChart } from './drawChart';

interface TestsProps {}

const Tests: FC<TestsProps> = () => {
  const [noInstances, setNoInstances] = useState<number>(10);
  const [noNodes, setNoNodes] = useState<number>(50);
  const [maxCapacity, setMaxCapacity] = useState<number>(100);
  const [results, setResults] = useState<TestResult[]>([]);
  const [sortingKey, setSortingKey] = useState<'noNodes'>('noNodes');
  const [showFordFulkerson, setShowFordFulkerson] = useState<boolean>(true);
  const [showEdmondsKarp, setShowEdmondsKarp] = useState<boolean>(true);
  const [showDinic, setShowDinic] = useState<boolean>(true);
  const [showGoldbergTarjan, setShowGoldbergTarjan] = useState<boolean>(true);
  const [showLandau, setShowLandau] = useState<boolean>(true);
  const [aggregate, setAggregate] = useState<boolean>(true);
  const [useLinearYAxis, setUseLinearYAxis] = useState<boolean>(false);

  const { width: contentWidth, height: contentHeight, ref: contentRef } = useResizeDetector();

  const handleNoInstancesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number.parseInt(e.target.value, 10);
    setNoInstances(n);
  };

  const handleNoNodesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number.parseInt(e.target.value, 10);
    setNoNodes(n);
  };

  const handleMaxCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = Number.parseInt(e.target.value, 10);
    setMaxCapacity(n);
  };

  const runTests = () => {
    setResults((old) => {
      let newResults: Array<TestResult> = JSON.parse(JSON.stringify(old));
      let testIssuesLog = '';
      for (let i = 0; i < noInstances; i += 1) {
        const result = runTest(noNodes, maxCapacity);
        newResults.push(result);
        if (!result.sameFlowValueAcrossAlgorithms.testTest)
          testIssuesLog += 'Same flow test issue!\n';
        for (let i = 0; i < 4; i += 1) {
          if (!result.individualResults[i].hasSaturatedCut.testTest)
            testIssuesLog += 'Saturated cut test issue!\n';
          if (!result.individualResults[i].satisfiesCapacity.testTest)
            testIssuesLog += 'Capacity test issue!\n';
          if (!result.individualResults[i].satisfiesPreservation.testTest)
            testIssuesLog += 'Preservation test issue!\n';
        }
      }
      if (testIssuesLog.length > 0) alert(testIssuesLog);
      newResults = newResults.sort((a, b) => a[sortingKey] - b[sortingKey]);
      return newResults;
    });
  };

  const handleFileChange = (files: any[]) => {
    const pendingTexts: Promise<string>[] = [];
    for (const file of files) {
      pendingTexts.push(file.text());
    }
    for (const pendingText of pendingTexts) {
      pendingText.then((text) => {
        // Yep, no input sanitization.
        setResults((old) => [...old, ...JSON.parse(text)].sort((a, b) => a.noNodes - b.noNodes));
      });
    }
  };

  const handleFileError = (err: any, file: any) => {
    console.error(`error code ${err.code}: ${err.message}`);
  };

  let aggregatedResults: any;
  if (aggregate) {
    aggregatedResults = aggregateResults(results);
  }

  drawChart(
    contentRef,
    contentWidth,
    contentHeight,
    aggregate,
    aggregatedResults,
    results,
    showFordFulkerson,
    showEdmondsKarp,
    showDinic,
    showGoldbergTarjan,
    useLinearYAxis
  );

  return (
    <div className={styles.Tests} data-testid="Tests">
      <div id="d3-content" ref={contentRef} className={styles.content} />
      <div className={styles.newTestSetControls}>
        <div>
          <p>Instances</p>
          <input
            type="number"
            value={noInstances}
            onChange={handleNoInstancesChange}
            min={1}
            step={1}
          />
          <p>Nodes per Instance</p>
          <input type="number" value={noNodes} onChange={handleNoNodesChange} min={4} step={1} />
          <p>Max Capacity per Node</p>
          <input
            type="number"
            value={maxCapacity}
            onChange={handleMaxCapacityChange}
            min={1}
            step={1}
          />
          <button type="button" onClick={runTests} className={styles.run}>
            <FontAwesomeIcon icon={faGear} />
            Run Tests
          </button>
          <Files
            className={styles.upload}
            onChange={handleFileChange}
            onError={handleFileError}
            accepts={['application/json']}
            multiple
            clickable>
            <FontAwesomeIcon icon={faUpload} /> Upload
          </Files>
          <button type="button" onClick={() => setResults([])}>
            <FontAwesomeIcon icon={faBroom} />
            Clear Results
          </button>
        </div>
        <div>
          <p>Display Settings</p>
          <button
            type="button"
            onClick={() => setShowFordFulkerson((old) => !old)}
            className={showFordFulkerson ? styles.on : styles.off}>
            {showFordFulkerson ? (
              <FontAwesomeIcon icon={faEye} />
            ) : (
              <FontAwesomeIcon icon={faEyeSlash} />
            )}
            Ford-Fulkerson
          </button>
          <button
            type="button"
            onClick={() => setShowEdmondsKarp((old) => !old)}
            className={showEdmondsKarp ? styles.on : styles.off}>
            {showEdmondsKarp ? (
              <FontAwesomeIcon icon={faEye} />
            ) : (
              <FontAwesomeIcon icon={faEyeSlash} />
            )}
            Edmonds-Karp
          </button>
          <button
            type="button"
            onClick={() => setShowDinic((old) => !old)}
            className={showDinic ? styles.on : styles.off}>
            {showDinic ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEyeSlash} />}
            Dinic
          </button>
          <button
            type="button"
            onClick={() => setShowGoldbergTarjan((old) => !old)}
            className={showGoldbergTarjan ? styles.on : styles.off}>
            {showGoldbergTarjan ? (
              <FontAwesomeIcon icon={faEye} />
            ) : (
              <FontAwesomeIcon icon={faEyeSlash} />
            )}
            Goldberg-Tarjan
          </button>
          <button
            type="button"
            onClick={() => setUseLinearYAxis((old) => !old)}
            className={useLinearYAxis ? styles.on : styles.off}>
            {useLinearYAxis ? (
              <FontAwesomeIcon icon={faCheck} />
            ) : (
              <FontAwesomeIcon icon={faXmark} />
            )}
            Linear Runtime Axis
          </button>
          <button
            type="button"
            onClick={() => setAggregate((old) => !old)}
            className={aggregate ? styles.on : styles.off}>
            {aggregate ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faXmark} />}
            Aggregate
          </button>
        </div>
      </div>
    </div>
  );
};

export default Tests;
